import { KeyboardEvent, useCallback, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import theme from 'config/theme';
import Icon, { IconNames } from 'components/Icon';
import { useLogout } from 'hooks/useLogout';
import { useCloseOnOutsideInteraction } from 'hooks/useCloseOnOutsideInteraction';
import { getPathWithRef } from 'helpers/utils';

export const AccountMenu = () => {
  const { handleLogout } = useLogout();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // Stable reference: an inline closure here would change every render,
  // making the hook's effect tear down and re-attach its listeners on every
  // render instead of only when isOpen actually changes.
  const close = useCallback(() => setIsOpen(false), []);
  useCloseOnOutsideInteraction(containerRef, isOpen, close);

  // Refocuses the trigger, since unmounting the panel would otherwise drop
  // focus to <body>; stopPropagation avoids double-firing the hook's own Escape close.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="account-menu" ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        className="account-menu-trigger"
        onClick={() => setIsOpen(open => !open)}
        aria-label="Account menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={panelId}
      >
        <Icon
          name={isOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
          size={20}
          color={theme.colors.white}
        />
      </button>
      {isOpen && (
        <div className="account-menu-panel" id={panelId}>
          <Link
            to={getPathWithRef('/profile/edit')}
            className="account-menu-item"
            onClick={() => setIsOpen(false)}
          >
            Account Settings
          </Link>
          <button type="button" className="account-menu-item" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      )}
    </div>
  );
};
